import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { PLAYLIST_STATUS } from 'shared/const';

import SelectExistingPlaylist from './components/SelectExistingPlaylist';
import { createPlaylist } from '../../../context/graphql/mutations';
import { listPlaylists } from './graphql/queries';
import { usePlaylist } from '../../../context';

const CopyFromExistingPlaylist = () => {
  const [formStatus, setFormStatus] = useState();

  const { t } = useTranslation();
  const {
    dialogs: { setOpenCopyPlaylistForm },
    isGlobalOrganization,
    managerSelected,
    organization,
    useGraphQLMutation,
    useLazyGraphQLQuery,
  } = usePlaylist();

  const filter = {
    administrativeView: true,
    status: PLAYLIST_STATUS.TEMPLATE,
  };
  const limit = 100;

  const [listPlaylistsQuery, listPlaylistsResponse] = useLazyGraphQLQuery(
    listPlaylists,
    {
      fetchPolicy: 'no-cache',
    }
  );

  const [createPlaylistMutation, createPlaylistResponse] = useGraphQLMutation(
    createPlaylist,
    {
      onCompleted: () => {
        setFormStatus({
          success: true,
          message: t('playlist.copyPlaylistSuccessMessage'),
        });
      },
      onError: () => {
        setFormStatus({
          success: false,
          message: t('playlist.copyPlaylistFailureMessage'),
        });
      },
      update(cache, { data: { createPlaylist } }, { variables }) {
        const { input } = variables;
        const listVariables1 = {
          filter: {
            administrativeView: true,
            status: ['DRAFT', 'PUBLISHED', 'PAUSED', 'TEMPLATE'],
          },
        };
        const listVariables2 = { filter: { ...listVariables1.filter } };

        if (input.organizationId && input.ownerId) {
          listVariables1.filter.organizationId = input.organizationId;
          listVariables2.filter.organizationId = input.organizationId;
          listVariables2.filter.ownerId = input.ownerId;
        } else {
          listVariables1.filter.isGlobal = true;
          listVariables2.filter.isGlobal = true;
          listVariables2.filter.type = input.type;
        }

        const olderData1 = cache.readQuery({
          query: listPlaylists,
          variables: listVariables1,
        });
        const olderData2 = cache.readQuery({
          query: listPlaylists,
          variables: listVariables2,
        });

        if (olderData1) {
          cache.writeQuery({
            query: listPlaylists,
            variables: listVariables1,
            data: {
              listPlaylists: [...olderData1.listPlaylists, createPlaylist],
            },
          });
        }
        if (olderData2) {
          cache.writeQuery({
            query: listPlaylists,
            variables: listVariables2,
            data: {
              listPlaylists: [...olderData2.listPlaylists, createPlaylist],
            },
          });
        }
      },
    }
  );

  const onCloseHandler = () => {
    setOpenCopyPlaylistForm(false);
    setFormStatus(null);
  };

  const handleSubmit = (playlistSelected) => {
    const input = {
      templateId: playlistSelected,
    };

    if (!isGlobalOrganization) {
      input.ownerId = managerSelected;
      input.organizationId = organization.id;
    }

    createPlaylistMutation({
      variables: {
        input,
      },
    });
  };

  useEffect(() => {
    listPlaylistsQuery({
      variables: {
        filter,
        pagination: {
          limit,
          offset: 0,
        },
      },
    });
  }, []);

  useEffect(() => {
    const { data, fetchMore, loading } = listPlaylistsResponse;
    if (
      !loading &&
      data?.listPlaylists &&
      data.listPlaylists.length > 0 &&
      data.listPlaylists.length % limit === 0
    ) {
      fetchMore({
        variables: {
          filter,
          pagination: {
            limit,
            offset: data.listPlaylists.length,
          },
        },
      });
    }
  }, [listPlaylistsResponse]);

  return (
    <SelectExistingPlaylist
      onClose={onCloseHandler}
      existingPlaylists={listPlaylistsResponse.data?.listPlaylists || []}
      existingPlaylistsLoading={listPlaylistsResponse.loading}
      handleSubmit={handleSubmit}
      submitting={createPlaylistResponse.loading}
      formStatus={formStatus}
    />
  );
};

export default CopyFromExistingPlaylist;
